import {useMemo} from 'react';
import {trpc} from '@common-web/lib/trpc';
import {Badge} from '@common-web/components/ui/badge';
import {Skeleton} from '@common-web/components/ui/skeleton';
import {Checkbox} from '@common-web/components/ui/checkbox';
import {SCOPE_LABELS, isScopeCompatible} from '@common/permissions';
import type {RoleScope} from './types';

export function PermissionPicker({
  roleScope,
  selectedIds,
  onChange,
  disabled,
  lockedIds,
}: {
  roleScope: RoleScope;
  selectedIds: ReadonlySet<string>;
  onChange: (next: Set<string>) => void;
  disabled?: boolean;
  /**
   * Permission ids the role already holds that must not be removed
   * (protected keys like `tenant.access_all`). Their checkboxes render
   * checked and disabled; the server enforces the same rule.
   */
  lockedIds?: ReadonlySet<string>;
}) {
  const {data, isLoading} = trpc.internal.permissions.list.useQuery({
    page: 1,
    pageSize: 200,
    isDeprecated: false,
  });

  const grouped = useMemo(() => {
    const groups: Record<
      string,
      Array<{id: string; key: string; name: string; description: string | null; scope: RoleScope}>
    > = {};
    for (const p of data?.permissions ?? []) {
      const list = (groups[p.scope] ??= []);
      list.push({
        id: p.id,
        key: p.key,
        name: p.name,
        description: p.description,
        scope: p.scope as RoleScope,
      });
    }
    for (const list of Object.values(groups)) {
      list.sort((a, b) => a.name.localeCompare(b.name));
    }
    return groups;
  }, [data]);

  if (isLoading) {
    return <Skeleton className='h-32 w-full' />;
  }

  const scopes = (Object.keys(grouped) as RoleScope[]).sort();

  return (
    <div className='space-y-4'>
      {scopes.map((scope) => {
        const compatible = isScopeCompatible(roleScope, scope);
        return (
          <div key={scope} className='border rounded-md'>
            <div className='flex items-center justify-between px-3 py-2 bg-gray-50 border-b'>
              <div className='flex items-center gap-2'>
                <Badge variant='outline' className='text-xs'>
                  {SCOPE_LABELS[scope] ?? scope}
                </Badge>
                <span className='text-sm font-medium text-brand-navy'>
                  {grouped[scope].length} permission{grouped[scope].length === 1 ? '' : 's'}
                </span>
              </div>
              {!compatible && (
                <span className='text-xs text-brand-gray italic'>
                  Only available for Internal roles
                </span>
              )}
            </div>
            <div className='p-3 space-y-2'>
              {grouped[scope].map((p) => {
                const checked = selectedIds.has(p.id);
                const locked = checked && (lockedIds?.has(p.id) ?? false);
                return (
                  <label
                    key={p.id}
                    className={`flex items-start gap-2 cursor-pointer ${
                      compatible && !locked ? '' : 'opacity-50 cursor-not-allowed'
                    }`}
                  >
                    <Checkbox
                      checked={checked}
                      disabled={disabled || !compatible || locked}
                      onCheckedChange={(value) => {
                        const next = new Set(selectedIds);
                        if (value) next.add(p.id);
                        else next.delete(p.id);
                        onChange(next);
                      }}
                    />
                    <div className='flex flex-col leading-tight'>
                      <span className='text-sm font-medium text-brand-navy'>{p.name}</span>
                      {p.description && (
                        <span className='text-xs text-brand-gray line-clamp-1'>
                          {p.description}
                        </span>
                      )}
                      {locked && (
                        <span className='text-xs text-amber-600'>
                          Protected — cannot be removed from this role.
                        </span>
                      )}
                    </div>
                  </label>
                );
              })}
            </div>
          </div>
        );
      })}
    </div>
  );
}
